/**
 * CLM-91 / R-91-1 / D-91-1, D-91-2, D-91-8 — the cloudEQ entity dimension.
 *
 * Which of cloudEQ's legal entities signed a contract is a **lookup with
 * provenance, not an AI fact**: the converted `contract.full_text` is scanned
 * in-tenant — the preamble (first 6 000 chars) and the signature block (last
 * 4 000 chars) — against the `clm.cloudeq_entity` registry (legal names,
 * aliases, company numbers, registered-address fragments). No model, no
 * `SCHEMA_VERSION` bump, no `field_verification` rows (D-91-1). The result rides
 * in the store-conversion UPDATE for every contract, `processing_restricted`
 * ones included (rule 4 withholds only the reasoning step), and an admin value
 * (`cloudeq_entity_source = 'admin'`) is never overwritten by a scan (D-91-4).
 *
 * The matcher lives here, not in `apps/api`, so the web can explain an
 * Unassigned row with the same function later and so `packages/db`'s static
 * test can read the CHECK literals by path (`sql-static-cloudeq-entity.test.ts`
 * reads `CLOUDEQ_ENTITY_SOURCES` — keep the `export const X = [...] as const;`
 * form on one line).
 *
 * ## Scoring (D-91-8, as corrected by the critic)
 *
 * Every `legal_name`, alias and `company_number` string is entity-specific by
 * construction, so they weigh the same: `name_hits` = distinct such strings found
 * in the union of both windows. `address_hits` = distinct registered-address
 * fragments found. `score = 3 × name_hits + 1 × address_hits`. Match iff exactly
 * one entity has `name_hits ≥ 1`, **or** several do and the leader's score
 * exceeds the runner-up's by ≥ 3 (one more distinct name than the affiliate it
 * merely mentions). Address hits alone never match. Otherwise `null` with a note:
 * `no_match` when no entity has a name hit, `ambiguous:<keys in sort_order>`
 * otherwise. Generic tokens ("cloudEQ" alone, "the Supplier") are never in
 * `aliases` — they identify the company, not the entity.
 */
import { z } from 'zod';
/** Mirrors `ck_cloudeq_entity_key` (0095). */
export const CLOUDEQ_ENTITY_KEY_PATTERN = /^CEQ-[A-Z0-9]{2,4}$/;
export const CloudeqEntityKeySchema = z.string().regex(CLOUDEQ_ENTITY_KEY_PATTERN, 'expected a cloudEQ entity key');
/** Mirrors `ck_contract_cloudeq_entity_source` (0095). */
export const CLOUDEQ_ENTITY_SOURCES = ['text_scan', 'admin'];
export const CloudeqEntitySourceSchema = z.enum(CLOUDEQ_ENTITY_SOURCES);
/** One `clm.cloudeq_entity` row as the API returns it (`list-cloudeq-entities`). */
export const CloudeqEntitySchema = z
    .object({
    entity_key: CloudeqEntityKeySchema,
    display_name: z.string().min(1).max(60),
    legal_name: z.string().min(3).max(160),
    aliases: z.array(z.string().min(1)),
    company_number: z.string().nullable(),
    registered_address: z.string().nullable(),
    country_code: z.string().length(2),
    is_active: z.boolean(),
    sort_order: z.number().int(),
})
    .strict();
/** The candidate windows of D-91-1: the preamble, then the signature block. */
export const CLOUDEQ_ENTITY_PREAMBLE_CHARS = 6000;
export const CLOUDEQ_ENTITY_SIGNATURE_CHARS = 4000;
export const CLOUDEQ_ENTITY_NO_MATCH_NOTE = 'no_match';
export const CLOUDEQ_ENTITY_AMBIGUOUS_PREFIX = 'ambiguous:';
const NAME_WEIGHT = 3;
const ADDRESS_WEIGHT = 1;
const MARGIN = 3;
const MIN_ADDRESS_FRAGMENT = 4;
/**
 * NFKC, case-fold, drop `.` and `,` everywhere (so `Ltd.` ≡ `Ltd`, `cloudEQ, Inc.` ≡
 * `cloudEQ Inc`, `Pvt. Ltd.` ≡ `Pvt Ltd`), collapse whitespace, trim. Applied to
 * the text and to every registry string alike, so both sides agree.
 */
export function normaliseForCloudeqScan(value) {
    return value
        .normalize('NFKC')
        .toLowerCase()
        .replace(/[.,]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}
function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
/** Whole-token containment: `cloudeq inc` must not match inside `cloudeq incorporated`. */
function containsToken(haystack, needle) {
    if (needle.length === 0)
        return false;
    const pattern = new RegExp(`(^|[^a-z0-9])${escapeRegExp(needle)}($|[^a-z0-9])`, 'u');
    return pattern.test(haystack);
}
function distinctNormalised(values, minLength) {
    const out = new Set();
    for (const v of values) {
        if (v === null || v === undefined)
            continue;
        const n = normaliseForCloudeqScan(v);
        if (n.length >= minLength)
            out.add(n);
    }
    return [...out];
}
/** The union of the two windows, joined so a name straddling the boundary is not fabricated. */
export function cloudeqScanWindows(fullText) {
    const preamble = fullText.slice(0, CLOUDEQ_ENTITY_PREAMBLE_CHARS);
    const signature = fullText.length > CLOUDEQ_ENTITY_SIGNATURE_CHARS ? fullText.slice(-CLOUDEQ_ENTITY_SIGNATURE_CHARS) : '';
    return normaliseForCloudeqScan(`${preamble}\n\n${signature}`);
}
/**
 * D-91-8 — pure, deterministic; the same text and registry always give the same
 * answer (FR-27 spirit). Inactive registry rows are skipped.
 */
export function scanCloudeqEntity(fullText, registry) {
    const haystack = cloudeqScanWindows(fullText);
    const active = [...registry].filter((e) => e.is_active).sort((a, b) => a.sort_order - b.sort_order);
    const hits = active.map((entity) => {
        const names = distinctNormalised([entity.legal_name, ...entity.aliases, entity.company_number], 1);
        const fragments = distinctNormalised((entity.registered_address ?? '').split(/[,\n]/u), MIN_ADDRESS_FRAGMENT);
        const nameHits = names.filter((n) => containsToken(haystack, n)).length;
        const addressHits = fragments.filter((f) => containsToken(haystack, f)).length;
        return { entity, nameHits, addressHits, score: NAME_WEIGHT * nameHits + ADDRESS_WEIGHT * addressHits };
    });
    const scores = {};
    for (const h of hits)
        scores[h.entity.entity_key] = h.score;
    const named = hits.filter((h) => h.nameHits >= 1);
    if (named.length === 0) {
        return { entity_key: null, note: CLOUDEQ_ENTITY_NO_MATCH_NOTE, scores };
    }
    if (named.length === 1) {
        return { entity_key: named[0].entity.entity_key, note: null, scores };
    }
    const ranked = [...named].sort((a, b) => b.score - a.score || a.entity.sort_order - b.entity.sort_order);
    const leader = ranked[0];
    const runnerUp = ranked[1];
    if (leader.score - runnerUp.score >= MARGIN) {
        return { entity_key: leader.entity.entity_key, note: null, scores };
    }
    const note = `${CLOUDEQ_ENTITY_AMBIGUOUS_PREFIX}${named.map((h) => h.entity.entity_key).join(',')}`.slice(0, 200);
    return { entity_key: null, note, scores };
}
/**
 * The generic company token — the `[SUPPLIER]` spec's generic name, which is
 * deliberately never a registry alias (D-91-8).
 */
export const CLOUDEQ_GENERIC_TOKEN = 'cloudeq';
/** The token, bounded on both sides by a non-letter, non-digit (or the end) — `_` counts as a boundary. */
const GENERIC_TOKEN_PATTERN = new RegExp(`(?:^|[^\\p{L}\\p{N}])${CLOUDEQ_GENERIC_TOKEN}(?:$|[^\\p{L}\\p{N}])`, 'u');
/**
 * CLM-113 · R-113-25 — does a name a vendor row would import name cloudEQ
 * itself? True when, under `normaliseForCloudeqScan`, it **equals** a registry
 * `legal_name` or alias (active or not — a retired entity's name is still
 * cloudEQ's), or it **contains the token `cloudeq`** bounded by
 * non-alphanumerics.
 *
 * Why it refuses: `[SUPPLIER]` is in every FR-29 map, so a vendor party named
 * like a registry entry makes every contract filed against it fail at
 * pseudonymise (`AmbiguousAliasError`), and one that merely contains `cloudEQ`
 * competes with `[SUPPLIER]` for cloudEQ's own name. The runner's invariant is
 * that cloudEQ is never a counterparty party.
 *
 * The token test runs on the normalised form **and** on the NFKC, lower-cased
 * raw text, because `normaliseForCloudeqScan` drops `.` and `,` — so
 * `cloudEQ.Partners` would otherwise read `cloudeqpartners` and slip past. Both
 * readings refuse; refusing is the conservative direction. A name that merely
 * contains the letters inside a word (`Mycloudeqtech`) is not cloudEQ's.
 */
export function namesCloudeqEntity(name, registry) {
    const normalised = normaliseForCloudeqScan(name);
    if (normalised.length === 0)
        return false;
    const raw = name.normalize('NFKC').toLowerCase();
    if (GENERIC_TOKEN_PATTERN.test(normalised) || GENERIC_TOKEN_PATTERN.test(raw))
        return true;
    return registry.some((entity) => [entity.legal_name, ...entity.aliases].some((registered) => normaliseForCloudeqScan(registered) === normalised));
}
//# sourceMappingURL=cloudeq-entity.js.map